import React, { useState } from 'react';

interface ConsentModalProps {
    onClose: () => void;
    onSubmit: (consentId: number, consentGiven: boolean, sharingPreferences: any) => void;
    consentId?: number;
}

const ConsentModal: React.FC<ConsentModalProps> = ({ onClose, onSubmit, consentId = 0 }) => {
    const [sharingPreferences, setSharingPreferences] = useState({
        share_demographics: true,
        share_medical_history: true,
        share_medications: true,
        share_lab_results: true,
        share_imaging: true,
        share_notes: true
    });

    const [notes, setNotes] = useState('');

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setSharingPreferences(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const handleSubmit = (consentGiven: boolean) => {
        onSubmit(consentId, consentGiven, {
            ...sharingPreferences,
            consent_notes: notes
        });
        onClose();
    };

    return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            <i className="fas fa-shield-alt me-2 text-primary"></i>
                            Information Sharing Consent
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    
                    <div className="modal-body">
                        <div className="alert alert-info">
                            <i className="fas fa-info-circle me-2"></i>
                            <strong>Important:</strong> You control what information is shared with healthcare providers. 
                            You can change these settings at any time.
                        </div>

                        <h6 className="text-primary mb-3">
                            <i className="fas fa-user-shield me-2"></i>
                            What information would you like to share?
                        </h6>

                        <div className="row g-3">
                            <div className="col-md-6">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        name="share_demographics"
                                        checked={sharingPreferences.share_demographics}
                                        onChange={handleInputChange}
                                    />
                                    <label className="form-check-label">
                                        <strong>Demographics</strong>
                                        <br />
                                        <small className="text-muted">Age, gender, contact information</small>
                                    </label>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        name="share_medical_history"
                                        checked={sharingPreferences.share_medical_history}
                                        onChange={handleInputChange}
                                    />
                                    <label className="form-check-label">
                                        <strong>Medical History</strong>
                                        <br />
                                        <small className="text-muted">Past conditions, surgeries, allergies</small>
                                    </label>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        name="share_medications"
                                        checked={sharingPreferences.share_medications}
                                        onChange={handleInputChange}
                                    />
                                    <label className="form-check-label">
                                        <strong>Current Medications</strong>
                                        <br />
                                        <small className="text-muted">Prescriptions, dosages, frequency</small>
                                    </label>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        name="share_lab_results"
                                        checked={sharingPreferences.share_lab_results}
                                        onChange={handleInputChange}
                                    />
                                    <label className="form-check-label">
                                        <strong>Lab Results</strong>
                                        <br />
                                        <small className="text-muted">Blood tests, urine tests, etc.</small>
                                    </label>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        name="share_imaging"
                                        checked={sharingPreferences.share_imaging}
                                        onChange={handleInputChange}
                                    />
                                    <label className="form-check-label">
                                        <strong>Imaging</strong>
                                        <br />
                                        <small className="text-muted">X-rays, MRIs, CT scans</small>
                                    </label>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        name="share_notes"
                                        checked={sharingPreferences.share_notes}
                                        onChange={handleInputChange}
                                    />
                                    <label className="form-check-label">
                                        <strong>Clinical Notes</strong>
                                        <br />
                                        <small className="text-muted">Doctor's notes, observations</small>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="mt-4">
                            <label className="form-label">
                                <i className="fas fa-comment me-2"></i>
                                Additional Notes (Optional)
                            </label>
                            <textarea
                                className="form-control"
                                rows={3}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Any additional comments or concerns about information sharing..."
                            />
                        </div>

                        <div className="alert alert-warning mt-3">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            <strong>Note:</strong> You can always change these settings later. 
                            Your healthcare providers will only see the information you've consented to share.
                        </div>
                    </div>
                    
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button 
                            type="button" 
                            className="btn btn-danger me-2" 
                            onClick={() => handleSubmit(false)}
                        >
                            <i className="fas fa-times me-2"></i>
                            Deny Consent
                        </button>
                        <button 
                            type="button" 
                            className="btn btn-success" 
                            onClick={() => handleSubmit(true)}
                        >
                            <i className="fas fa-check me-2"></i>
                            Give Consent
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConsentModal; 